export type TeamId = 'A' | 'B' | 'C';
export type ClientRole = 'admin' | 'projector' | 'team';

export type GameStatus =
  | 'idle'
  | 'selecting_subject'
  | 'question_countdown'
  | 'question_active'
  | 'team_answering'
  | 'steal_mode'
  | 'round_end'
  | 'incorrect_answer';

export type RoundPhase = 'roulette' | 'playing' | 'steal' | 'round_end' | 'countdown';

export interface Question {
  id: number;
  subject_id: number;
  subject_name: string;
  grade: string;
  question_text: string;
  answer: string;
  options: string[];
  points: number;
}

export interface Subject {
  id: number;
  name: string;
  grade: string;
  color: string;
}

export interface GameState {
  gameId: string;
  status: GameStatus;
  currentQuestion: Question | null;
  currentSubject: Subject | null;
  winner: { team: TeamId; serverTs: number } | null;
  buzzer: 'closed' | 'open' | 'locked';
  blockedTeams: TeamId[];
  attemptedTeams: TeamId[];
  scores: Record<TeamId, number>;
  round: number;
  grade: string;
  timerExpiresAt: number | null;
  subjects: Subject[];
  roundPhase: RoundPhase;
  teamOrder: TeamId[];
  teamQueue: TeamId[];
  usedSubjects: number[];
  // TURN SYSTEM EXTENSIONS (frontend)
  currentTurnIndex: number;
  buzzerActive: boolean;
  stealWindow: boolean;
  firstTurnTeam: TeamId | null;
  questionsThisRound: number;
  isSuddenDeath?: boolean;
  /** Server epoch ms injected at send time — used by clients to compute clock offset */
  serverNow?: number;
}

export type ServerMsg =
  | { type: 'STATE_SYNC'; state: GameState }
  | { type: 'BUZZER_WON'; winner: TeamId; serverTs: number }
  | { type: 'BUZZER_OPEN' }
  | { type: 'BUZZER_CLOSED' }
  | { type: 'SCORE_UPDATE'; scores: Record<TeamId, number> }
  | { type: 'QUESTION_SET'; question: Question }
  | { type: 'STEAL_ACTIVATED'; unblocked: TeamId[] }
  | { type: 'ROUND_END'; winner: TeamId | null; correct: boolean; points: number }
  | { type: 'RESET' }
  | { type: 'STATUS_CHANGE'; status: GameStatus }
  | { type: 'TIMER_START'; expiresAt: number }
  | { type: 'TIMER_STOP' }
  | { type: 'SUBJECT_SELECTED'; subject: Subject }
  | { type: 'ERROR'; message: string };

export type ClientMsg =
  | { type: 'BUZZ'; team: TeamId }
  | { type: 'ADMIN_OPEN_BUZZER' }
  | { type: 'ADMIN_CLOSE_BUZZER' }
  | { type: 'ADMIN_CONFIRM_ANSWER'; correct: boolean }
  | { type: 'ADMIN_STEAL' }
  | { type: 'ADMIN_FINISH_QUESTION' }
  | { type: 'ADMIN_SELECT_QUESTION'; questionId: number }
  | { type: 'ADMIN_SELECT_SUBJECT'; subjectId: number }
  | { type: 'ADMIN_NEXT_ROUND' }
  | { type: 'ADMIN_NEXT_QUESTION' }
  | { type: 'ADMIN_START_ROUND' }
  | { type: 'ADMIN_SUBMIT_TURN_ANSWER'; correct: boolean }
  | { type: 'ADMIN_RESET' }
  | { type: 'ADMIN_SET_GRADE'; grade: string }
  | { type: 'ADMIN_SPIN_ROULETTE' }
  | { type: 'ADMIN_START_TIMER'; seconds: number }
  | { type: 'ADMIN_STOP_TIMER' }
  | { type: 'ADMIN_TOGGLE_SUDDEN_DEATH' };

export const TEAM_COLORS: Record<TeamId, { bg: string; text: string; light: string }> = {
  A: { bg: '#3b82f6', text: '#fff', light: '#dbeafe' },
  B: { bg: '#ef4444', text: '#fff', light: '#fee2e2' },
  C: { bg: '#10b981', text: '#fff', light: '#d1fae5' },
};

export const TEAM_NAMES: Record<TeamId, string> = {
  A: 'Equipo A',
  B: 'Equipo B',
  C: 'Equipo C',
};

export const STATUS_LABELS: Record<GameStatus, string> = {
  idle:               'Esperando inicio',
  selecting_subject:  'Eligiendo materia',
  question_countdown: '\u00a1Prep\u00e1rate!',
  question_active:    'Buzzer activo',
  team_answering:     'Equipo respondiendo',
  steal_mode:         'Modo robo',
  round_end:          'Fin de ronda',
  incorrect_answer:   'Respuesta incorrecta',
};

export const ROUND_PHASE_LABELS: Record<RoundPhase, string> = {
  roulette:   'Sorteo de equipos',
  playing:    'Jugando',
  steal:      'Modo robo',
  round_end:  'Fin de ronda',
  countdown:  '¡Prepárate!',
};

